import React, { useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { toast, ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import './Login.css'

const Login = () => {
  const [isLogin, setIsLogin] = useState(true)
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone_number: '',
    password: '',
    confirmPassword: '',
    gender: '',
    role: 'student'
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const history = useHistory()

  // Cleanup on unmount to prevent memory leaks
  useEffect(() => {
    let isMounted = true

    return () => {
      isMounted = false
    }
  }, [])

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    // Validation for registration
    if (!isLogin) {
      if (formData.password !== formData.confirmPassword) {
        setError('Passwords do not match')
        setLoading(false)
        return
      }
      if (formData.password.length < 8) {
        setError('Password must be at least 8 characters long')
        setLoading(false)
        return
      }
    }

    try {
      const endpoint = isLogin ? '/api/auth/login' : '/api/auth/register'
      const payload = isLogin
        ? { 
            email: formData.email, 
            password: formData.password 
          }
        : {
            full_name: formData.full_name,
            email: formData.email,
            phone_number: formData.phone_number || null,
            password: formData.password,
            gender: formData.gender || null,
            role: formData.role
          }

      const response = await fetch(`${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      })

      const data = await response.json()

      if (response.ok) {
        // Handle roles that require admin approval (volunteer, teacher, invigilator)
        if (!isLogin && ['volunteer', 'teacher', 'invigilator'].includes(data.user.role) && !data.user.is_active) {
          const roleName = data.user.role.charAt(0).toUpperCase() + data.user.role.slice(1)
          toast.success(`Registration successful! Your ${roleName.toLowerCase()} account is pending admin approval. You will be notified once approved.`, {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          })
          setLoading(false)
          setIsLogin(true) // Switch back to login
          return
        }
        
        localStorage.setItem('access_token', data.access_token)
        localStorage.setItem('refresh_token', data.refresh_token)
        localStorage.setItem('user', JSON.stringify(data.user))
        
        // Redirect based on user role (no state update after this)
        if (data.user.role === 'admin') {
          history.push('/admin-dashboard')
        } else if (data.user.role === 'teacher') {
          history.push('/teacher-dashboard')
        } else if (data.user.role === 'invigilator') {
          history.push('/invigilator-dashboard')
        } else if (data.user.role === 'volunteer') {
          history.push('/volunteer-dashboard')
        } else {
          history.push('/student-dashboard')
        }
      } else {
        // Handle rejection message
        if (data.rejection_reason) {
          toast.error(`Your volunteer account has been rejected. Reason: ${data.rejection_reason}`, {
            position: "top-right",
            autoClose: 7000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          })
        }
        setError(data.error || 'An error occurred')
        setLoading(false)
      }
    } catch (err) {
      setError('Network error. Please try again.')
      setLoading(false)
    }
  }

  return (
    <div className="login-container">
      <ToastContainer />
      <div className={`login-card ${isLogin ? 'login-mode' : 'registration-mode'}`}>
        <button 
          className="back-to-home-button" 
          onClick={() => history.push('/')}
          type="button"
        >
          <span className="back-arrow">←</span>
          <span>Back to Home</span>
        </button>
        
        <div className="login-header">
          <img
            src="/rts-logo.png"
            alt="Rapid Testing Service"
            className="login-logo"
          />
          <h2>{isLogin ? 'Login' : 'Registration'}</h2>
          {!isLogin && <p>Create your account</p>}
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="login-form">
          {!isLogin ? (
            // Registration Form - Two Column Layout
            <>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="full_name">Full Name</label>
                  <input
                    type="text"
                    id="full_name"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleChange}
                    required
                    placeholder="Enter your full name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="Enter your email"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="phone_number">Phone Number (Optional)</label>
                  <input
                    type="tel"
                    id="phone_number"
                    name="phone_number"
                    value={formData.phone_number}
                    onChange={handleChange}
                    placeholder="Enter your number"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="role">Role</label>
                  <select
                    id="role"
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    required
                  >
                    <option value="student">Student</option>
                    <option value="teacher">Teacher</option>
                    <option value="invigilator">Invigilator</option>
                    <option value="volunteer">Volunteer</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="password">Password</label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    placeholder="Enter your password"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirm Password</label>
                  <input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                    placeholder="Confirm your password"
                  />
                </div>
              </div>

              <div className="form-group-full">
                <label>Gender</label>
                <div className="radio-group">
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="gender"
                      value="male"
                      checked={formData.gender === 'male'}
                      onChange={handleChange}
                    />
                    <span>Male</span>
                  </label>
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="gender"
                      value="female"
                      checked={formData.gender === 'female'}
                      onChange={handleChange}
                    />
                    <span>Female</span>
                  </label>
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="gender"
                      value="prefer_not_to_say"
                      checked={formData.gender === 'prefer_not_to_say'}
                      onChange={handleChange}
                    />
                    <span>Prefer not to say</span>
                  </label>
                </div>
              </div>

              <button type="submit" className="login-button" disabled={loading}>
                {loading ? 'Please wait...' : 'Register'}
              </button>
            </>
          ) : (
            // Login Form - Single Column
            <>
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="Enter your email"
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">Password</label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  placeholder="Enter your password"
                />
              </div>

              <button type="submit" className="login-button" disabled={loading}>
                {loading ? 'Please wait...' : 'Login'}
              </button>
            </>
          )}
        </form>

        <div className="login-footer">
          <p>
            {isLogin ? "Don't have an account? " : "Already have an account? "}
            <button 
              type="button" 
              className="toggle-button"
              onClick={() => setIsLogin(!isLogin)}
            >
              {isLogin ? 'Register' : 'Login'}
            </button>
          </p>
        </div>
      </div>
    </div>
  )
}

export default Login
